import _ from 'lodash';
import orComparator from '../../../shared/util/lodash/merge/orComparator';
import useMyWorkspace from '../../workspace/hooks/useMyWorkspace';
import AlarmAPI from '../AlarmAPI';
import type { TypedUseQuerySubscriptionOptions } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import StructureAPI from '../../structure/StructureAPI';
import { getDevicesIdsFromGroupSelector } from '../../structure/selectors/Group.selector';
import { useAppSelector } from '../../store.model';
import {
  ALARMS_POLLING_INTERVAL,
  AlarmsQueryParamsAPIRequest,
  AlarmState,
  excludedEventIdsStr,
} from '../Alarm.model';
import { useParams } from 'react-router-dom';
import { patchLoadingState } from '../../../service/device/CommanderAPI/hooks/useAirConnectStatusCount';

type Options = TypedUseQuerySubscriptionOptions;

function useAlarmsByGroup(
  args: {
    groupId?: string;
    workspaceId?: string;
    params?: AlarmsQueryParamsAPIRequest;
  },
  options: Options = {}
) {
  const { data: workspaceData, ...restWorkspaceAPI } = useMyWorkspace(
    undefined,
    options
  );
  const { marketId } = useParams<{ marketId: string }>();
  const workspaceId = args.workspaceId ?? marketId ?? workspaceData?.id;
  const { data: devicesAPI, ...restDevicesAPI } =
    StructureAPI.useGetStructureV2Query(args.workspaceId, options);
  const devicesIdsFromGroup = useAppSelector((state) =>
    getDevicesIdsFromGroupSelector(state, {
      workspaceId: args.workspaceId,
      groupId: args?.groupId ?? '',
    })
  );
  const { data: alarmsData, ...restAlarmAPI } =
    AlarmAPI.useGetFlexeserveIncidentByGroupIdQuery(
      {
        workspaceId: workspaceId ?? '',
        groupId: args?.groupId ?? '',
        params: {
          state: AlarmState.NEW,
          page_size: 1000,
          excluded_event_ids: excludedEventIdsStr,
          ...args.params,
        },
      },
      {
        pollingInterval: ALARMS_POLLING_INTERVAL,
        refetchOnMountOrArgChange: true,
        ...options,
        skip: !workspaceId || !args?.groupId || options.skip,
      }
    );

  const allAlarms = useMemo(
    () =>
      alarmsData?.data?.filter?.((alarm) =>
        devicesIdsFromGroup?.includes(alarm.deviceId)
      ),
    [alarmsData, devicesIdsFromGroup]
  );

  const response = _.chain(patchLoadingState(restAlarmAPI))
    .mergeWith(patchLoadingState(restDevicesAPI), orComparator)
    .mergeWith(patchLoadingState(restWorkspaceAPI), orComparator)
    .value();
  return patchLoadingState({
    ...response,
    currentData: restAlarmAPI?.currentData,
    data: alarmsData?.data ?? [],
    count: alarmsData?.count ?? 0,
    allAlarms,
  });
}

export default useAlarmsByGroup;
